import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Camera, PlayCircle } from "lucide-react";
import api, { resolveMedia } from "@/lib/api";
import useSEO, { stripHtml, injectJsonLd, removeJsonLd } from "@/lib/seo";
import RichText from "@/components/RichText";
import SocialShare from "@/components/SocialShare";

function youtubeId(url) {
  const m = String(url || "").match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : "";
}
function vimeoId(url) {
  const m = String(url || "").match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : "";
}

function VideoEmbed({ url }) {
  const yid = youtubeId(url);
  if (yid) {
    return (
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${yid}`}
        title="YouTube"
        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        className="w-full h-full"
      />
    );
  }
  const vid = vimeoId(url);
  if (vid) {
    return (
      <iframe
        src={`https://player.vimeo.com/video/${vid}`}
        title="Vimeo"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        className="w-full h-full"
      />
    );
  }
  return (
    <div className="w-full h-full flex items-center justify-center text-neutral-500 text-sm">
      Nicht unterstützte Video-URL: {url}
    </div>
  );
}

export default function BauchronikDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    setNotFound(false);
    api.get(`/bauchroniken/${slug}`)
      .then((r) => setItem(r.data))
      .catch((e) => { if (e.response?.status === 404) setNotFound(true); });
  }, [slug]);

  useSEO({
    title: item?.title || "Bauchronik",
    description: stripHtml(item?.description || "", 220) || "Bauchronik-Eintrag zum Quartier am Hornschen Tor.",
    path: `/bauchroniken/${slug}`,
    image: item?.header_image_url ? resolveMedia(item.header_image_url) : undefined,
    type: "article",
  });

  useEffect(() => {
    if (!item) return;
    injectJsonLd("ld-bauchronik", {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": item.title,
      "datePublished": item.date_iso || item.created_at,
      "dateModified": item.updated_at,
      "image": item.header_image_url ? [resolveMedia(item.header_image_url)] : undefined,
      "publisher": {
        "@type": "GovernmentOrganization",
        "name": "Stadt Detmold",
      },
    });
    return () => removeJsonLd("ld-bauchronik");
  }, [item]);

  if (notFound) {
    return (
      <div className="container-narrow py-24 text-center" data-testid="bauchronik-detail-notfound">
        <p className="text-neutral-500 mb-6">Dieser Bauchronik-Eintrag wurde nicht gefunden.</p>
        <Link to="/bauchroniken" className="inline-flex items-center gap-2 text-sm text-[hsl(var(--brand-green-strong))] hover:underline">
          <ArrowLeft className="h-4 w-4" /> Zurück zur Übersicht
        </Link>
      </div>
    );
  }

  if (!item) {
    return <div className="container-narrow py-24" data-testid="bauchronik-detail-loading"><div className="h-4 w-32 bg-neutral-100 animate-pulse" /></div>;
  }

  const HEADER = item.header_image_url ? resolveMedia(item.header_image_url) : "";
  const images = (item.media || []).filter((m) => m.type === "image" && m.url);
  const videos = (item.media || []).filter((m) => m.type === "video" && m.url);
  const shareUrl = `${window.location.origin}/bauchroniken/${slug}`;

  return (
    <div className="bg-white" data-testid="bauchronik-detail-page">
      {/* Header */}
      {HEADER ? (
        <section className="relative h-[46vh] sm:h-[52vh] min-h-[300px] max-h-[520px] overflow-hidden bg-neutral-900">
          <img src={HEADER} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.10) 45%, rgba(255,255,255,0.98) 100%)" }} />
          <div className="absolute inset-0 flex items-end">
            <div className="container-narrow pb-8 lg:pb-12 max-w-4xl w-full">
              <button
                onClick={() => nav("/bauchroniken")}
                data-testid="bauchronik-detail-back"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-white/90 hover:text-white mb-4"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Bauchroniken
              </button>
              <p className="text-xs uppercase tracking-[0.20em] text-[hsl(var(--brand-green))] mb-2 inline-flex items-center gap-1.5 drop-shadow-md">
                <Calendar className="h-3 w-3" /> {item.date || ""}
              </p>
              <h1 className="font-semibold text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.05] max-w-3xl drop-shadow-[0_2px_20px_rgba(0,0,0,0.35)]" data-testid="bauchronik-detail-title">
                {item.title}
              </h1>
            </div>
          </div>
        </section>
      ) : (
        <div className="container-narrow pt-14 pb-4">
          <Link to="/bauchroniken" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-neutral-500 hover:text-neutral-800 mb-3">
            <ArrowLeft className="h-3.5 w-3.5" /> Bauchroniken
          </Link>
          <p className="text-xs uppercase tracking-[0.15em] text-[hsl(var(--brand-green-strong))] mb-2 inline-flex items-center gap-1.5">
            <Calendar className="h-3 w-3" /> {item.date || ""}
          </p>
          <h1 className="font-semibold text-3xl sm:text-4xl lg:text-5xl text-neutral-900 leading-[1.05]" data-testid="bauchronik-detail-title">
            {item.title}
          </h1>
        </div>
      )}

      {/* Body */}
      <article className="container-narrow py-10 lg:py-14 max-w-3xl">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="text-sm text-neutral-500 inline-flex items-center gap-4">
            {images.length > 0 && (<span className="inline-flex items-center gap-1.5"><Camera className="h-4 w-4" /> {images.length + (HEADER ? 1 : 0)} Foto{images.length + (HEADER ? 1 : 0) === 1 ? "" : "s"}</span>)}
            {videos.length > 0 && (<span className="inline-flex items-center gap-1.5"><PlayCircle className="h-4 w-4" /> {videos.length} Video{videos.length === 1 ? "" : "s"}</span>)}
          </div>
          <SocialShare
            url={shareUrl}
            title={item.title}
            text={stripHtml(item.description || "", 160)}
            testid="bauchronik-share"
          />
        </div>

        {item.description && (
          <RichText html={item.description} className="prose-quartier text-neutral-700 leading-relaxed" />
        )}
      </article>

      {/* Video embeds */}
      {videos.length > 0 && (
        <section className="container-narrow pb-12 max-w-4xl" data-testid="bauchronik-videos">
          <h2 className="font-semibold text-2xl sm:text-3xl mb-6">Videos</h2>
          <div className="space-y-8">
            {videos.map((v, i) => (
              <figure key={i} className="border border-brand-line overflow-hidden">
                <div className="relative w-full aspect-video bg-neutral-900">
                  <VideoEmbed url={v.url} />
                </div>
                {v.caption && <figcaption className="px-4 py-3 text-sm text-neutral-600 bg-neutral-50 border-t border-brand-line">{v.caption}</figcaption>}
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* Photo gallery */}
      {images.length > 0 && (
        <section className="container-narrow pb-16 lg:pb-24 max-w-6xl" data-testid="bauchronik-gallery">
          <h2 className="font-semibold text-2xl sm:text-3xl mb-6">Fotos</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {images.map((img, i) => (
              <button
                key={i}
                onClick={() => setLightbox(img)}
                className="group relative aspect-square overflow-hidden bg-neutral-100 border border-brand-line"
                data-testid={`bauchronik-photo-${i}`}
              >
                <img
                  src={resolveMedia(img.url)}
                  alt={img.caption || item.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {img.caption && (
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent px-3 py-2 text-[11px] text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    {img.caption}
                  </div>
                )}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          role="dialog"
          className="fixed inset-0 z-[1000] bg-black/90 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
          data-testid="bauchronik-lightbox"
        >
          <img src={resolveMedia(lightbox.url)} alt={lightbox.caption || ""} className="max-w-full max-h-full object-contain" />
          {lightbox.caption && <div className="absolute bottom-8 left-8 right-8 text-center text-sm text-white/90">{lightbox.caption}</div>}
        </div>
      )}
    </div>
  );
}
